// ============================================================================
//  Cliente del tablero — TypeScript sin framework, compilado con esbuild.
//  Puerto de ../tracking-fan/controlador/app/web/index.html (615 líneas,
//  HTML+CSS+JS inline en el piloto): acá se separa en vista (index.njk) +
//  este archivo, pero se conserva el mismo diseño — Canvas 2D a mano, sin
//  librería de gráficos, socket empuja el snapshot completo cada segundo.
//
//  Los tipos del snapshot vienen de src/shared/types: si el servidor cambia
//  un campo, esto deja de compilar en vez de fallar en silencio en pantalla.
// ============================================================================
import type {
  StateSnapshot,
  FanSnapshot,
  GasKey,
  GasesSnapshot,
  PortalGasSnapshot,
  VfdSnapshot,
  ShiftProgressSnapshot,
  GasRawPoint,
  SettingsPayload,
} from '../../src/shared/types';
import { GAS_THRESHOLDS, PRESENCE_SILENT_SECONDS } from '../../src/config/constants';
import { esc, postJson, getJson, toast } from '../shared/api';

declare const io: (() => { on: (event: string, cb: (data: unknown) => void) => void }) | undefined;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

// ============================================================================
//  Estado local del cliente (histórico + firma anti-parpadeo del ventilador)
// ============================================================================
interface HistorySample {
  ts: number;
  workerCount: number;
  vehicleCount: number;
  level: number | null;
  hz: number | null;
}

let hist: HistorySample[] = [];
// mini-gráficas de gases: /api/gas-history (gas_samples), aparte de history_samples
let gasHist: GasRawPoint[] = [];
let fansSig = '';

// ============================================================================
//  Render principal — se llama por cada 'state' del socket (1/s)
// ============================================================================
function render(snap: StateSnapshot): void {
  renderConnection(snap);
  renderFans(snap);
  renderOcc(snap);
  renderGases(snap);
  renderVfd(snap);
  renderEnergy(snap);
  renderScanners(snap);
  renderDetected(snap);
  renderAlerts(snap);
}

function renderConnection(snap: StateSnapshot): void {
  const pill = $('mqtt-pill');
  if (pill) {
    pill.textContent = `MQTT · ${snap.mqttConnected ? 'conectado' : snap.mqttStatus || 'sin conexión'}`;
    pill.classList.toggle('pill-ok', snap.mqttConnected);
    pill.classList.toggle('pill-danger', !snap.mqttConnected);
  }
  const banner = $('mqtt-banner');
  if (banner) {
    const unconfigured = !snap.mqttConnected && snap.mqttStatus === 'sin configurar';
    banner.classList.toggle('show', unconfigured);
  }
}

// ==========================================================================
//  Ventilador — firma anti-parpadeo: measuredHz/pending se excluyen de la
//  firma (cambian cada segundo) y se parchean por id en cada tick, para que
//  el repintado de 1 Hz nunca mate un clic en vuelo sobre los botones de modo.
// ==========================================================================
function fanHzHtml(f: FanSnapshot): string {
  return f.measuredHz != null
    ? `${f.measuredHz.toFixed(1)}<small> ${esc(f.unit || 'Hz')} reales</small>`
    : `${(f.value ?? 0).toFixed(0)}<small> ${esc(f.unit || '')}</small>`;
}

function fanPendingText(f: FanSnapshot): string {
  const p = f.pending;
  if (!p) return '';
  const t =
    p.remainingSeconds >= 120
      ? `${Math.ceil(p.remainingSeconds / 60)} min`
      : `${p.remainingSeconds} s`;
  const v = `${p.targetValue.toFixed(0)} ${f.unit || 'Hz'}`;
  return p.dir === 'up' ? `subirá a ${v} en ${t}` : `bajará a ${v} en ${t}`;
}

/** Apagado pendiente: solo se muestra cuando falta poco (< 10 min), para no
 *  tener un contador corriendo toda la jornada con la labor vacía. */
function fanOffText(f: FanSnapshot): string {
  if (f.offInSeconds === null || f.offInSeconds > 600) return '';
  const t = f.offInSeconds >= 120 ? `${Math.ceil(f.offInSeconds / 60)} min` : `${f.offInSeconds} s`;
  return `se apagará en ${t}`;
}

function fanSubHtml(f: FanSnapshot): string {
  const consigna =
    f.measuredHz != null ? `consigna: ${(f.value ?? 0).toFixed(0)} ${esc(f.unit || 'Hz')}` : '';
  return [consigna, esc(f.reason || ''), esc(fanPendingText(f)), esc(fanOffText(f))]
    .filter(Boolean)
    .join(' · ');
}

function fanBlock(f: FanSnapshot): string {
  const n = f.levels.length;
  let segs = '';
  for (let i = 0; i < n; i++) {
    let cls = 'seg';
    if (i <= f.level) cls += f.level >= n - 1 ? ' max' : ' on';
    segs += `<div class="${cls}"></div>`;
  }
  const manualButtons = f.levels
    .map(
      (v, i) =>
        `<button class="btn sm ${i === f.manualLevel ? 'act' : ''}" data-act="set-mode" data-id="${f.id}" data-mode="manual" data-level="${i}">${v.toFixed(0)}</button>`,
    )
    .join('');
  const powerChip = f.statusTopic
    ? `<span class="dim" style="color:var(--${f.powered ? 'ok' : 'warn'})">· ${f.powered ? 'encendido' : 'APAGADO'}</span>`
    : '<span class="dim" style="color:var(--warn)">· sin tópico de estado</span>';
  return `<div class="fanblk">
    <div class="fan-head"><b>${esc(f.name)}</b>
      <span class="dim">${f.mode === 'manual' ? 'modo manual' : 'automático'}</span>
      ${powerChip}
      ${f.area ? `<span class="dim">· bocamina: ${esc(f.area)}</span>` : ''}
      ${f.commandTopic ? '' : '<span class="dim" style="color:var(--warn)">· sin tópico de salida</span>'}</div>
    <div class="fan-hz" id="fhz-${f.id}">${fanHzHtml(f)}</div>
    <div class="fan-sub" id="fsub-${f.id}">${fanSubHtml(f)}</div>
    <div class="segs">${segs}</div>
    <div class="seg-lbl"><span>mínimo</span><span>máximo</span></div>
    ${f.failsafe ? '<div class="badge-fs show"><svg class="i"><use href="#ic-alert"/></svg> MODO SEGURO activo</div>' : ''}
    <div class="mode">
      <button class="btn ${f.mode === 'auto' ? 'act' : ''}" data-act="set-mode" data-id="${f.id}" data-mode="auto">Automático</button>
      <button class="btn ${f.mode === 'manual' ? 'act' : ''}" data-act="set-mode" data-id="${f.id}" data-mode="manual">Manual</button>
    </div>
    ${
      f.mode === 'manual'
        ? `<div class="manual show">${manualButtons}</div>
      ${
        f.statusTopic
          ? `<div class="mode">
        <button class="btn ${f.powered ? 'act' : ''}" data-act="set-power" data-id="${f.id}" data-on="1">Encender</button>
        <button class="btn ${f.powered ? '' : 'act'}" data-act="set-power" data-id="${f.id}" data-on="0">Apagar</button>
      </div>`
          : ''
      }
      <div class="fan-sub" style="color:var(--warn)">En manual, la seguridad puede elevar el nivel pero nunca bajarlo, y puede encender el ventilador aunque esté apagado.</div>`
        : ''
    }
  </div>`;
}

function renderFans(snap: StateSnapshot): void {
  const title = $('fans-title');
  if (title) title.textContent = snap.fans.length > 1 ? 'Ventiladores' : 'Ventilador';

  const sig = JSON.stringify(
    // offInSeconds cambia cada segundo, igual que measuredHz/pending: fuera
    // de la firma, se parchea en fanSubHtml (ver comentario de arriba).
    snap.fans.map((f) => ({
      ...f,
      measuredHz: undefined,
      pending: undefined,
      offInSeconds: undefined,
    })),
  );
  if (sig !== fansSig) {
    fansSig = sig;
    const el = $('fans');
    if (el) el.innerHTML = snap.fans.map(fanBlock).join('');
  }
  for (const f of snap.fans) {
    const h = $(`fhz-${f.id}`);
    if (h) h.innerHTML = fanHzHtml(f);
    const s = $(`fsub-${f.id}`);
    if (s) s.innerHTML = fanSubHtml(f);
  }
}

// ==========================================================================
//  Ocupación / dentro
// ==========================================================================
function renderOcc(snap: StateSnapshot): void {
  const p = $('n-persons');
  if (p) p.textContent = String(snap.persons ?? '—');
  const v = $('n-vehicles');
  if (v) v.textContent = String(snap.vehicles ?? '—');

  const areaNames = Object.keys(snap.areas || {});
  const areasEl = $('occ-areas');
  if (areasEl) {
    if (areaNames.length > 1) {
      areasEl.style.display = 'block';
      areasEl.innerHTML = areaNames
        .sort()
        .map((n) => {
          const a = snap.areas[n];
          return `<div class="fanmap-row"><b>${esc(n)}</b> — ${a?.persons ?? 0} p · ${a?.vehicles ?? 0} v</div>`;
        })
        .join('');
    } else {
      areasEl.style.display = 'none';
      areasEl.innerHTML = '';
    }
  }
  renderTransit(snap);
  renderInside(snap);
}

/** "En tránsito" — balizas con dirección detectada (secuencia de escáneres)
 *  aún sin confirmar por el escáner terminal ni por el timeout de silencio.
 *  Ver specs/20260807-02-direccion-por-orden-de-escaneres.md. */
function renderTransit(snap: StateSnapshot): void {
  const el = $('occ-transit');
  if (!el) return;
  const transit = snap.transit ?? [];
  if (!transit.length) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  el.style.display = 'block';
  const entrando = transit.filter((t) => t.direction === 'in').length;
  const saliendo = transit.filter((t) => t.direction === 'out').length;
  el.innerHTML = `<div class="fanmap-row"><b>En tránsito</b> — ${entrando} entrando · ${saliendo} saliendo</div>`;
}

function renderInside(snap: StateSnapshot): void {
  const el = $('inside-list');
  if (!el) return;
  if (!snap.inside.length) {
    el.innerHTML = '<div class="empty">Nadie dentro de la mina.</div>';
    return;
  }
  const sorted = [...snap.inside].sort((a, b) => a.sinceAt - b.sinceAt);
  el.innerHTML = sorted
    .map((b) => {
      const who = b.name || b.code || b.beaconMac;
      const kind = b.subjectType === 'vehicle' ? 'vehículo' : 'persona';
      // Nadie sale solo por silencio (ver presence.service.ts): si la baliza
      // enmudeció, se marca y el operador decide — puede seguir abajo con la
      // baliza muerta, o haber salido por una vía sin lector.
      const silent = snap.ts - b.lastSeenAt > PRESENCE_SILENT_SECONDS;
      return `<div class="inside-row">
        <span class="chip ${b.subjectType === 'vehicle' ? 'chip-warn' : 'chip-ok'}">${kind}</span>
        <span>${esc(who)}</span>
        <span class="chip chip-dim">${esc(b.area || '—')}</span>
        <span class="chip chip-dim">${fmtDuration(snap.ts - b.sinceAt)}</span>
        ${
          silent
            ? `<span class="chip chip-silent">sin reportar hace ${fmtDuration(snap.ts - b.lastSeenAt)}</span>
               <button type="button" class="btn btn-sm" data-act="exit-presence"
                 data-mac="${esc(b.beaconMac)}" data-who="${esc(who)}">Sacar</button>`
            : ''
        }
      </div>`;
    })
    .join('');
}

function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min`;
  return `${s} s`;
}

function fmtAgo(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} días`;
}

// ==========================================================================
//  Gases — unidades/etiquetas/decimales viven en el cliente (igual que el
//  piloto), pero los umbrales se importan de src/config/constants.ts en vez
//  de duplicarlos a mano.
// ==========================================================================
const GAS_LABEL: Record<GasKey, string> = { co: 'CO', co2: 'CO₂', o2: 'O₂', no2: 'NO₂' };
const GAS_DECIMALS: Record<GasKey, number> = { co: 0, co2: 2, o2: 1, no2: 1 };
const GAS_KEYS_ORDER: GasKey[] = ['co', 'co2', 'o2', 'no2'];
const CLASS_COLOR: Record<string, string> = {
  normal: 'verde',
  atencion: 'amarillo',
  peligro: 'rojo',
  sin_dato: 'gris',
};
const CLASS_LABEL: Record<string, string> = {
  normal: 'Normal',
  atencion: 'Atención',
  peligro: 'Peligro',
  sin_dato: 'Sin dato',
};

function gasTile(
  values: Partial<Record<GasKey, number>>,
  classes: Record<GasKey, string>,
  key: GasKey,
): string {
  const cls = classes[key] ?? 'sin_dato';
  const color = CLASS_COLOR[cls] ?? 'gris';
  const v = values[key];
  return `<div class="gas ${color}"><div class="nm">${GAS_LABEL[key]}</div>
    <div class="v">${v == null ? '—' : v.toFixed(GAS_DECIMALS[key])}<small> ${GAS_THRESHOLDS[key].unit}</small></div>
    <div class="st">${CLASS_LABEL[cls] ?? cls}</div></div>`;
}

function renderGases(snap: StateSnapshot): void {
  const gases: GasesSnapshot = snap.gases;
  // solo bocaminas con al menos un gas configurado — la entrada '' (lo sin
  // asignar) siempre existe en el snapshot aunque esté vacía, ver
  // control.service.ts::gasStates.
  const portalNames = Object.keys(gases.portals).filter(
    (name) => (gases.portals[name] as PortalGasSnapshot).configured.length > 0,
  );
  const el = $('gases');
  if (el) {
    if (portalNames.length <= 1) {
      el.innerHTML = GAS_KEYS_ORDER.map((k) => gasTile(gases.values, gases.classes, k)).join('');
    } else {
      el.innerHTML = portalNames
        .map((name) => {
          const d: PortalGasSnapshot = gases.portals[name] as PortalGasSnapshot;
          const dot = d.stale ? 'rojo' : d.statusLight;
          const tiles = GAS_KEYS_ORDER.filter((k) => d.configured.includes(k))
            .map((k) => gasTile(d.values, d.classes, k))
            .join('');
          const label = name || 'sin bocamina';
          return `<div style="grid-column:1/-1"><div class="det-h"><span class="dot ${dot}"></span>
            Bocamina ${esc(label)} ${d.stale ? '· <span style="color:var(--danger)">sin datos</span>' : ''}</div>
            <div class="gases">${tiles || '<div class="empty">Sin gases configurados.</div>'}</div></div>`;
        })
        .join('');
    }
  }
  const age = $('gas-age');
  if (age) {
    const ageS = gases.lastUpdate ? Math.round(snap.ts - gases.lastUpdate) : null;
    age.innerHTML = gases.stale
      ? '<span style="color:var(--danger);font-weight:700">sin datos — modo seguro</span>'
      : ageS != null
        ? `hace ${ageS}s`
        : 'esperando nodo…';
  }
}

// ==========================================================================
//  VFD — reutiliza el tile .gas
// ==========================================================================
const VFD_LABEL: Record<string, string> = {
  frequency: 'Frecuencia',
  current: 'Corriente',
  voltage: 'Voltaje',
  power: 'Potencia',
  status: 'Estado',
};
const VFD_UNIT: Record<string, string> = {
  frequency: 'Hz',
  current: 'A',
  voltage: 'V',
  power: 'kW',
  status: '',
};

function vfdTile(variable: string, vf: VfdSnapshot): string {
  const v = vf.values[variable];
  const stale = vf.stale[variable];
  let disp = '—';
  if (v != null) disp = v.toFixed(variable === 'current' || variable === 'power' ? 1 : 0);
  let color = 'verde';
  if (stale || v == null) color = 'gris';
  else if (variable === 'status') color = /1|run|marcha|on/i.test(String(v)) ? 'verde' : 'amarillo';
  const st = stale
    ? `sin dato ${Math.round(vf.ageSeconds[variable] ?? 0)}s`
    : v == null
      ? 'esperando'
      : 'ok';
  return `<div class="gas ${color}"><div class="nm">${VFD_LABEL[variable] ?? esc(variable)}</div>
    <div class="v">${disp}<small> ${VFD_UNIT[variable] ?? ''}</small></div>
    <div class="st">${st}</div></div>`;
}

function renderVfd(snap: StateSnapshot): void {
  const el = $('vfd-tiles');
  const note = $('vfd-note');
  const withTelemetry = snap.fans.filter(
    (f) => (snap.vfds[String(f.id)]?.configured ?? []).length > 0,
  );
  if (!withTelemetry.length) {
    if (el)
      el.innerHTML =
        '<div class="empty">Sin telemetría configurada. Registra los tópicos del VFD en <a href="/config">Configuración</a>.</div>';
    if (note) note.textContent = '';
    return;
  }
  if (el) {
    el.innerHTML = withTelemetry
      .map((f) => {
        const vf = snap.vfds[String(f.id)] as VfdSnapshot;
        return vf.configured.map((variable) => vfdTile(variable, vf)).join('');
      })
      .join('');
  }
  const anyStale = withTelemetry.some((f) => {
    const vf = snap.vfds[String(f.id)] as VfdSnapshot;
    return vf.configured.some((v) => vf.stale[v]);
  });
  if (note) {
    note.textContent = anyStale ? 'hay datos viejos' : 'en vivo';
    note.style.color = anyStale ? 'var(--warn)' : 'var(--dim)';
  }
}

// ==========================================================================
//  Energía — tarjeta con estado vacío hasta que exista el módulo (fuera de
//  alcance del port actual, ver plan).
// ==========================================================================
function renderEnergy(snap: StateSnapshot): void {
  const src = $('energy-src');
  const body = $('energy-body');
  if (!snap.energy.configured) {
    if (src) src.textContent = '';
    if (body)
      body.innerHTML =
        '<div class="empty">Sin configurar. Registra la tarifa y la potencia nominal en <a href="/config">Configuración</a>.</div>';
    return;
  }
  if (src) src.textContent = `potencia: ${esc(snap.energy.source)}`;
  const e = snap.energy;
  const money = e.tariff > 0;
  const head = money
    ? `${esc(e.currency)} ${e.today.money.toFixed(2)}`
    : `${e.today.savings.toFixed(1)} kWh`;
  if (body) {
    body.innerHTML = `<div class="en-wrap">
      <div style="font-family:var(--mono);font-size:1.8rem;font-weight:700">${head}</div>
      <div class="kv">Últimos 30 días: ${money ? `${esc(e.currency)} ${e.last30Days.money.toFixed(2)}` : `${e.last30Days.savings.toFixed(1)} kWh`}</div>
      <div class="kv">Reducción 30 días: ${e.last30Days.pct.toFixed(0)}%</div>
    </div>`;
  }
}

// ==========================================================================
//  Ahorro del turno EN CURSO — lo que llega por el socket 'energy'
//  (ShiftProgressSnapshot, ver jobs/shift-progress-job.ts). A diferencia de
//  renderEnergy() de arriba (StateSnapshot.energy, tick de 1 Hz, siempre
//  vacío hoy — ver el comentario de CLAUDE.md), este evento es aparte:
//  llega cada ENERGY_PROGRESS_PERIOD_SECONDS (60 s) y se reenvía al conectar
//  (mismo criterio que 'portals'), así que puede tardar hasta 60 s en
//  aparecer tras cargar la página si el servidor recién arrancó.
//
//  Regla de negocio (ver CLAUDE.md "Cómo mostrar el ahorro de un
//  ShiftSummaryRow"): el número "oficial" es siempre savingsKwh/savingsCost
//  tal cual vienen — nunca se resta baselineFullKwh - realKwh acá.
// ==========================================================================
function fmtKwh(n: number | null): string {
  return n == null ? '—' : n.toFixed(2);
}

function fmtCoverage(ratio: number): string {
  const pct = Math.round(ratio * 100);
  const cls = ratio >= 0.95 ? 'pill-ok' : ratio >= 0.5 ? 'pill-warn' : 'pill-danger';
  return `<span class="pill ${cls}" style="padding:1px 8px;font-size:.72rem">${pct}%</span>`;
}

function renderShiftProgress(snap: ShiftProgressSnapshot): void {
  const age = $('shift-progress-age');
  if (age) {
    const shiftLabel = snap.shift === 'day' ? 'Día' : 'Noche';
    age.textContent = `turno ${shiftLabel} · ${fmtDuration(snap.elapsedSeconds)} de ${fmtDuration(snap.shiftSeconds)}`;
  }

  const summary = $('shift-progress-summary');
  if (summary) {
    const t = snap.totals;
    const savingsHtml =
      t.savingsKwh == null
        ? '<span class="kv">Sin línea base cargada para ningún ventilador — ver Configuración.</span>'
        : `<div style="font-family:var(--mono);font-size:1.6rem;font-weight:700">
             ${fmtKwh(t.savingsKwh)}<small style="font-size:.6em;font-weight:400"> kWh ahorrados</small>
           </div>
           <div class="kv">${esc(snap.currency)} ${t.savingsCost!.toFixed(2)} · real ${fmtKwh(t.realKwh)} kWh vs. línea base ${fmtKwh(t.baselineKwh)} kWh</div>`;
    const partial =
      t.fansWithoutBaseline > 0
        ? `<div class="kv">${t.fansWithoutBaseline} ventilador(es) sin línea base — no incluidos en el total.</div>`
        : '';
    summary.innerHTML = `${savingsHtml}${partial}`;
  }

  const rows = $('shift-progress-rows');
  const empty = $('shift-progress-empty');
  if (empty) empty.toggleAttribute('hidden', snap.fans.length > 0);
  if (rows) {
    rows.innerHTML = snap.fans
      .map((f) => {
        const savings =
          f.savingsKwh == null
            ? '—'
            : `${fmtKwh(f.savingsKwh)}${f.savingsKwh < 0 ? ' <span class="pill pill-warn" style="padding:1px 6px;font-size:.68rem">↑</span>' : ''}`;
        const cost =
          f.savingsCost == null ? '—' : `${esc(snap.currency)} ${f.savingsCost.toFixed(2)}`;
        return `
      <tr>
        <td>${esc(f.fanName)}</td>
        <td>${fmtCoverage(f.coverageRatio)}</td>
        <td class="mono">${fmtKwh(f.realKwh)}</td>
        <td class="mono">${fmtKwh(f.baselineKwh)}</td>
        <td class="mono">${savings}</td>
        <td class="mono">${cost}</td>
      </tr>`;
      })
      .join('');
  }
}

// ==========================================================================
//  Escáneres (readers)
// ==========================================================================
function renderScanners(snap: StateSnapshot): void {
  const el = $('scanners');
  if (!el) return;
  if (!snap.readers.length) {
    el.innerHTML = '<div class="empty">Sin lectores configurados.</div>';
    return;
  }
  el.innerHTML = snap.readers
    .map((r) => {
      const visto = r.online ? 'en línea' : `sin reportar hace ${fmtAgo(r.lastSeenSecondsAgo)}`;
      const zoneLabel = { exterior: 'exterior', middle: 'intermedio', interior: 'interior' };
      const zone = r.zone ? zoneLabel[r.zone] : 'sin zona';
      const area = r.area || 'sin bocamina';
      const warn = r.assigned ? '' : ' <span class="warn-chip">sin asignar</span>';
      return `<div class="fanmap-row"><b>${esc(r.tag)}</b>
        <span class="kv">${esc(area)} · ${esc(zone)} · orden ${r.sortOrder}</span>${warn}
        <span class="chip ${r.online ? 'chip-ok' : 'chip-danger'}">${visto}</span></div>`;
    })
    .join('');
}

// ==========================================================================
//  Detectados ahora — tabla capada a 15 filas
// ==========================================================================
const DET_MAX = 15;

function renderDetected(snap: StateSnapshot): void {
  const count = $('det-count');
  if (count) count.textContent = `${snap.detected.length} baliza(s)`;
  const el = $('detected');
  if (!el) return;
  if (!snap.detected.length) {
    el.innerHTML = '<div class="empty">Ninguna baliza en rango.</div>';
    return;
  }
  const rows = snap.detected.slice(0, DET_MAX);
  const extra = snap.detected.length - rows.length;
  el.innerHTML = `<div class="table-wrap"><table class="list dtab">
    <thead><tr><th>Baliza</th><th>Tipo</th><th>Zona</th><th>Señal</th><th>Estado</th></tr></thead>
    <tbody>${rows
      .map((d) => {
        const kind =
          d.subjectType === 'vehicle'
            ? 'vehículo'
            : d.subjectType === 'person'
              ? 'persona'
              : 'desconocido';
        const estado = d.assigned ? esc(d.name || 'asignado') : d.registered ? 'en stock' : 'nuevo';
        return `<tr>
          <td class="mono">${esc(d.code || d.beaconMac)}</td>
          <td><span class="chip ${d.subjectType}">${kind}</span></td>
          <td><span class="zbadge ${d.zone}">${esc(d.area)} · ${d.zone}</span></td>
          <td>${d.rssi ?? '—'} dBm</td>
          <td>${estado}</td>
        </tr>`;
      })
      .join('')}</tbody>
  </table>${extra > 0 ? `<div class="empty">… y ${extra} más</div>` : ''}</div>`;
}

// ==========================================================================
//  Alertas
// ==========================================================================
function renderAlerts(snap: StateSnapshot): void {
  const el = $('alerts');
  if (!el) return;
  el.innerHTML = snap.alerts
    .map(
      (a) => `<div class="alert ${a.severity}">
        <svg class="i"><use href="#ic-alert"/></svg><span class="msg">${esc(a.message)}</span>
        ${
          a.acknowledged
            ? '<span class="ackd">Reconocida</span>'
            : `<button class="btn btn-sm" data-act="ack-alert" data-key="${esc(a.key)}">Reconocer</button>`
        }
      </div>`,
    )
    .join('');
}

// ==========================================================================
//  Acciones — delegación de eventos, sin onclick inline (rompería con esbuild/CSP)
// ==========================================================================
document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  const btn = target.closest<HTMLElement>('[data-act]');
  if (!btn) return;
  const act = btn.dataset.act;

  if (act === 'set-mode') {
    const id = Number(btn.dataset.id);
    const mode = btn.dataset.mode as 'auto' | 'manual';
    const level = btn.dataset.level !== undefined ? Number(btn.dataset.level) : undefined;
    void postJson('/api/fan', { id, mode, level });
    return;
  }
  if (act === 'set-power') {
    void postJson('/api/fan', { id: Number(btn.dataset.id), power: btn.dataset.on === '1' });
    return;
  }
  if (act === 'ack-alert') {
    void postJson('/api/alerts/ack', { key: btn.dataset.key });
    return;
  }
  if (act === 'exit-presence') {
    const mac = btn.dataset.mac ?? '';
    const who = btn.dataset.who || mac;
    if (confirm(`¿Registrar la salida de ${who}? Solo hazlo si confirmaste que no está dentro.`)) {
      void postJson('/api/personal/presence/exit', { mac })
        .then(() => toast(`Salida registrada: ${who}`))
        .catch(() => toast('No se pudo registrar la salida', true));
    }
    return;
  }
  if (btn.id === 'reset-presence') {
    if (confirm('¿Reiniciar el conteo de personas y vehículos dentro?')) {
      void postJson('/api/personal/presence/reset', {});
    }
  }
});

// ==========================================================================
//  Instalación incompleta — /api/setup/status, cada 30 s
// ==========================================================================
interface SetupStatus {
  mqtt: { configured: boolean; connected: boolean; status: string };
  gas: { configured: boolean; hasData: boolean; ok: boolean };
  fans: { ok: boolean; detail: Array<{ id: number; name: string; topic: string; ok: boolean }> };
  complete: boolean;
}

function checkSetup(): void {
  getJson<SetupStatus>('/api/setup/status')
    .then((st) => {
      const banner = $('setup-banner');
      if (!banner) return;
      if (st.complete) {
        banner.classList.remove('show');
        return;
      }
      const falta: string[] = [];
      if (!st.mqtt.configured) falta.push('conexión al servidor');
      if (!st.gas.configured) falta.push('tópicos de gases');
      if (!st.fans.ok) falta.push('tópico de salida de algún ventilador');
      const el = $('setup-falta');
      if (el) el.textContent = falta.length ? `Falta: ${falta.join(', ')}.` : '';
      banner.classList.add('show');
    })
    .catch(() => undefined);
}

// ==========================================================================
//  Eventos — /api/events?limit=40, cada 8 s
// ==========================================================================
interface EventRow {
  id: number;
  ts: number;
  type: string;
  beaconMac: string;
  subjectType: string;
  detail: string;
}

function loadEvents(): void {
  getJson<EventRow[]>('/api/events?limit=40')
    .then((rows) => {
      const el = $('events');
      if (!el) return;
      if (!rows.length) {
        el.innerHTML = '<div class="empty">Sin eventos todavía.</div>';
        return;
      }
      el.innerHTML = rows
        .map((r) => {
          const time = new Date(r.ts * 1000).toLocaleTimeString('es-PE', { hour12: false });
          return `<div class="evt"><span class="k ${esc(r.type)}">${esc(r.type)}</span>
            <span>${time}</span> <span class="mono">${esc(r.beaconMac || '—')}</span>
            <span>${esc(r.detail)}</span></div>`;
        })
        .join('');
    })
    .catch(() => undefined);
}

// ==========================================================================
//  Histórico — cada 15 s: /api/history?minutes=60 (ventiladores + ocupación)
//  y /api/gas-history de la última hora (gases, por bocamina)
// ==========================================================================
// gas_samples es por bocamina y el tablero no tiene selector: las
// mini-gráficas muestran la PRIMERA bocamina con gases configurados. Se
// resuelve una vez por carga de página (cambiar la config pide recargar).
let gasPortal: Promise<number | null> | null = null;
function gasPortalId(): Promise<number | null> {
  gasPortal ??= getJson<SettingsPayload>('/api/settings')
    .then((s) => s.gasBindings.find((b) => b.portalId != null)?.portalId ?? null)
    .catch(() => {
      gasPortal = null; // reintentar en la próxima vuelta
      return null;
    });
  return gasPortal;
}

function loadHist(): void {
  getJson<HistorySample[]>('/api/history?minutes=60')
    .then((rows) => {
      hist = rows;
      drawChart();
    })
    .catch(() => undefined);
  gasPortalId()
    .then(async (portalId) => {
      const to = Date.now();
      gasHist =
        portalId == null
          ? []
          : (
              await getJson<{ rows: GasRawPoint[] }>(
                `/api/gas-history?portalId=${portalId}&bucket=raw&from=${to - 3_600_000}&to=${to}`,
              )
            ).rows;
      drawSparks();
    })
    .catch(() => undefined);
}

// ==========================================================================
//  Gráficos — Canvas 2D a mano, conscientes de devicePixelRatio
// ==========================================================================
function drawChart(): void {
  const cv = document.getElementById('chart') as HTMLCanvasElement | null;
  if (!cv) return;
  const dpr = window.devicePixelRatio || 1;
  const w = cv.clientWidth || 700;
  const h = cv.clientHeight || 150;
  cv.width = w * dpr;
  cv.height = h * dpr;
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);

  if (hist.length < 2) {
    ctx.fillStyle = '#5d6b7c';
    ctx.font = '12px system-ui';
    ctx.fillText('Recolectando datos…', 10, 24);
    return;
  }
  const t0 = hist[0]?.ts ?? 0;
  const t1 = hist[hist.length - 1]?.ts ?? t0 + 1;
  const span = Math.max(1, t1 - t0);
  const px = (t: number): number => 10 + ((t - t0) / span) * (w - 20);
  const maxHz = Math.max(60, ...hist.map((r) => r.hz ?? 0));
  const maxN = Math.max(4, ...hist.map((r) => Math.max(r.workerCount, r.vehicleCount)));

  const line = (
    key: 'hz' | 'workerCount' | 'vehicleCount',
    max: number,
    color: string,
    fill?: string,
  ): void => {
    ctx.beginPath();
    let started = false;
    for (const r of hist) {
      const val = r[key];
      if (val == null) continue;
      const x = px(r.ts);
      const y = 10 + (1 - val / max) * (h - 24);
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else {
        ctx.lineTo(x, y);
      }
    }
    if (fill) {
      ctx.lineTo(px(t1), h - 14);
      ctx.lineTo(px(t0), h - 14);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.beginPath();
      started = false;
      for (const r of hist) {
        const val = r[key];
        if (val == null) continue;
        const x = px(r.ts);
        const y = 10 + (1 - val / max) * (h - 24);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.6;
    ctx.stroke();
  };

  ctx.strokeStyle = '#26303d';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(10, h - 14);
  ctx.lineTo(w - 10, h - 14);
  ctx.stroke();

  line('hz', maxHz, '#cf7a3c', 'rgba(207,122,60,.10)');
  line('workerCount', maxN, '#4aa8d8');
  line('vehicleCount', maxN, '#e39a5f');
}

const SPARK_COLOR: Record<GasKey, string> = {
  co: '#4aa8d8',
  co2: '#e39a5f',
  o2: '#4cc46b',
  no2: '#e0a83b',
};

function drawSparks(): void {
  for (const k of GAS_KEYS_ORDER) {
    const cv = document.getElementById(`sp-${k}`) as HTMLCanvasElement | null;
    const label = $(`sv-${k}`);
    if (!cv) continue;
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth || 140;
    const h = cv.clientHeight || 40;
    cv.width = w * dpr;
    cv.height = h * dpr;
    const ctx = cv.getContext('2d');
    if (!ctx) continue;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const pts = gasHist.filter((r) => r[k] != null);
    if (label) {
      const last = pts[pts.length - 1];
      label.textContent = last ? (last[k] as number).toFixed(k === 'o2' ? 1 : 0) : '—';
    }
    if (pts.length < 2) {
      ctx.fillStyle = '#5d6b7c';
      ctx.font = '11px system-ui';
      ctx.fillText('sin datos', 4, h / 2 + 3);
      continue;
    }
    let lo = Math.min(...pts.map((r) => r[k] as number));
    let hi = Math.max(...pts.map((r) => r[k] as number));
    if (hi - lo < 1e-9) {
      lo -= 1;
      hi += 1;
    }
    const t0 = pts[0]?.ts ?? 0;
    const t1 = pts[pts.length - 1]?.ts ?? t0 + 1;
    const span = Math.max(1, t1 - t0);
    ctx.beginPath();
    pts.forEach((r, i) => {
      const x = ((r.ts - t0) / span) * (w - 4) + 2;
      const y = h - 4 - (((r[k] as number) - lo) / (hi - lo)) * (h - 8);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = SPARK_COLOR[k];
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

window.addEventListener('resize', () => {
  drawChart();
  drawSparks();
});

// ==========================================================================
//  Arranque
// ==========================================================================
if (typeof io === 'function') {
  const socket = io();
  socket.on('state', (data) => render(data as StateSnapshot));
  socket.on('energy', (data) => renderShiftProgress(data as ShiftProgressSnapshot));
} else {
  toast('socket.io no disponible: ¿se cargó /socket.io/socket.io.js?', true);
}

checkSetup();
loadEvents();
loadHist();
setInterval(checkSetup, 30000);
setInterval(loadEvents, 8000);
setInterval(loadHist, 15000);
